import { useEffect, useRef, useState } from 'react';
import { api, errorMessage } from '../lib/api';
import { PageHeader } from '../components/PageHeader';
import { Icon } from '../components/Icon';
import { Banner } from '../components/Banner';
import { useConfirm } from '../components/Confirm';
import { formatDuration } from '../lib/tests';

interface Instruction {
  module: 'LISTENING' | 'READING' | 'WRITING';
  part: number | null;
  url: string;
  filename: string | null;
  mimeType: string | null;
  durationMs: number | null;
  updatedAt: string;
}

/** Audio was what these used to be; a few centres may still have one. */
export const isVideo = (mimeType: string | null) => !mimeType?.startsWith('audio/');

const MODULES = [
  {
    id: 'LISTENING' as const,
    label: 'Listening',
    note: 'How the screen works, that the recording plays once, and that only the volume can be changed.',
  },
  {
    id: 'READING' as const,
    label: 'Reading',
    note: 'The sixty minutes, the split screen, and that there is no extra time to transfer answers.',
  },
  {
    id: 'WRITING' as const,
    label: 'Writing',
    note: 'The two tasks, the word counts, and that they divide the hour themselves.',
  },
];

/**
 * The instruction films every candidate watches, wherever they sit.
 *
 * Deliberately platform-wide and not something a centre can change: a
 * candidate should be shown the same thing in every centre, and a teacher who
 * could reword it would be changing the exam. Played before the clock starts,
 * so it costs nobody exam time.
 */
export function InstructionsPage() {
  const [rows, setRows] = useState<Instruction[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const { ask, dialog } = useConfirm();
  const inputs = useRef<Record<string, HTMLInputElement | null>>({});

  async function load() {
    try {
      const { data } = await api.get<{ instructions: Instruction[] }>('/instructions');
      setRows(data.instructions);
      setError(null);
    } catch (err) {
      setError(errorMessage(err, 'Could not load the instructions'));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function upload(module: string, file: File) {
    setBusy(module);
    setError(null);
    setNotice(null);
    try {
      const form = new FormData();
      form.append('module', module);
      form.append('file', file);
      await api.post('/instructions', form);
      setNotice(`${module[0]}${module.slice(1).toLowerCase()} instructions updated for every centre.`);
      await load();
    } catch (err) {
      setError(errorMessage(err, 'Could not upload that recording'));
    } finally {
      setBusy(null);
    }
  }

  async function remove(module: string) {
    const yes = await ask({
      title: `Remove the ${module.toLowerCase()} instructions?`,
      body: <p>Candidates will see nothing before that paper until another recording is uploaded.</p>,
      confirmLabel: 'Remove recording',
      tone: 'danger',
    });
    if (!yes) return;
    setBusy(module);
    try {
      await api.delete(`/instructions/${module}`);
      await load();
    } catch (err) {
      setError(errorMessage(err, 'Could not remove that recording'));
    } finally {
      setBusy(null);
    }
  }

  const forModule = (id: string) => rows.find((r) => r.module === id && r.part === null);

  return (
    <>
      {dialog}
      <PageHeader
        title="Instructions"
        meta={
          <p className="muted" style={{ maxWidth: '60ch' }}>
            Played once before a paper begins, in every centre. It runs before the clock starts, so
            watching it costs a candidate nothing.
          </p>
        }
      />

      <div className="stack">
        {error && <Banner tone="error">{error}</Banner>}
        {notice && <Banner tone="success">{notice}</Banner>}

        {loading ? (
          <p className="muted">Loading…</p>
        ) : (
          MODULES.map((module) => {
            const current = forModule(module.id);
            return (
              <section key={module.id} className="panel stack">
                <div>
                  <h2 className="panel__title">{module.label}</h2>
                  <p className="muted" style={{ maxWidth: '62ch' }}>
                    {module.note}
                  </p>
                </div>

                {current ? (
                  <>
                    <p className="mono muted">
                      {current.filename}
                      {current.durationMs ? ` · ${formatDuration(current.durationMs)}` : ''}
                    </p>
                    {isVideo(current.mimeType) ? (
                      <video controls preload="metadata" src={current.url} className="instruction__player" />
                    ) : (
                      <audio controls preload="none" src={current.url} style={{ width: '100%' }} />
                    )}
                  </>
                ) : (
                  <p className="muted">
                    Nothing uploaded. Candidates go straight to the paper with only the written
                    notes on screen.
                  </p>
                )}

                <input
                  ref={(el) => {
                    inputs.current[module.id] = el;
                  }}
                  type="file"
                  accept="video/*,audio/*"
                  hidden
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) void upload(module.id, file);
                    e.target.value = '';
                  }}
                />

                <div className="row">
                  <button
                    className="btn"
                    disabled={busy === module.id}
                    onClick={() => inputs.current[module.id]?.click()}
                  >
                    <Icon name="upload" size={16} />
                    {busy === module.id ? 'Uploading…' : current ? 'Replace video' : 'Upload video'}
                  </button>
                  {current && (
                    <button
                      className="btn btn--danger"
                      disabled={busy === module.id}
                      onClick={() => void remove(module.id)}
                    >
                      Remove
                    </button>
                  )}
                </div>
              </section>
            );
          })
        )}
      </div>
    </>
  );
}
